import { useQuery } from '@apollo/client';
import { DialogTitle } from '@headlessui/react';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import { Modal } from '@retainerclub/common';
import { GET_CART_ITEM_SUBSCRIPTION_TIERS } from '@retainerclub/customer-api';
import PropTypes from 'prop-types';
import { useMemo, useState } from 'react';
import { splitTiers } from './helpers/acknowledgmentCopy';
import {
  DISCLOSURE_VERSION,
  disclosureIntro,
  disclosureTitle,
  productNoun,
} from './helpers/disclosureCopy';
import SubscriptionDisclosure from './SubscriptionDisclosure';

// Headless UI calls setOpen on backdrop click and Escape; a non-dismissable
// modal simply ignores those.
function ignoreDismiss() {
  return undefined;
}

export const emptySubmission = {
  agreed: false,
  selectedSubscriptionProductId: null,
  lowerTiersExpanded: false,
};

// Modal for ONE plan or subscription item: a cart item at checkout (between
// Place Order and payment) or an order item on the dashboard. The parent shows
// it once per item, keyed by item id so state resets between items.
// onConfirm receives { itemId, agreed, disclosureVersion,
// selectedSubscriptionProductId, lowerTiersExpanded } for the
// createSubscriptionAcknowledgment mutation; the parent decides whether itemId
// is a cart item or order item.
function SubscriptionDisclosureModal({
  open,
  item = null,
  onBack,
  onConfirm,
  isSubmitting = false,
  errorMessages = null,
  position = null,
  dismissable = true,
  intro = null,
  positionScope = 'in your cart',
  requiresCard = false,
  hasCard = true,
  onAddCard = null,
  zdex = null,
  tiers: providedTiers = null,
}) {
  const [submission, setSubmission] = useState(emptySubmission);

  // Tiers come from the parent when it already resolved them (the dashboard
  // gate's pending-acknowledgments query). At checkout the item is a cart
  // item, and the server resolves its tiers against the same price book the
  // acknowledgment will record.
  const { data, loading } = useQuery(GET_CART_ITEM_SUBSCRIPTION_TIERS, {
    variables: { cartItemId: item?.id },
    skip: !open || Boolean(providedTiers) || !item?.id,
    fetchPolicy: 'network-only',
  });

  const tiers = useMemo(
    () =>
      [...(providedTiers || data?.cartItemSubscriptionTiers || [])].sort(
        (a, b) => a.price - b.price,
      ),
    [providedTiers, data?.cartItemSubscriptionTiers],
  );
  const product = item?.product;
  const hasAdditionalTiers =
    splitTiers({ product, tiers }).additional.length > 0;
  // Without a single tier there is no price to disclose, so the ledger row
  // would prove nothing; the server refuses it too.
  const canContinue =
    Boolean(product) &&
    tiers.length > 0 &&
    !requiresCard &&
    submission.agreed &&
    !loading &&
    !isSubmitting;
  const showSoftCardPrompt =
    !(requiresCard || hasCard) && onAddCard && productNoun(product) === 'plan';

  function handleBack() {
    setSubmission(emptySubmission);
    onBack();
  }

  function handleContinue() {
    if (!canContinue) {
      return;
    }

    onConfirm({
      itemId: item.id,
      agreed: submission.agreed,
      disclosureVersion: DISCLOSURE_VERSION,
      selectedSubscriptionProductId:
        submission.selectedSubscriptionProductId || null,
      // Only meaningful when something was collapsed; null otherwise so the
      // ledger does not claim a link that was never shown.
      lowerTiersExpanded: hasAdditionalTiers
        ? Boolean(submission.lowerTiersExpanded)
        : null,
    });
  }

  return (
    <Modal
      open={open}
      setOpen={dismissable ? handleBack : ignoreDismiss}
      size="5xl"
      showCloseButton={false}
      zdex={zdex}
    >
      <div className="mx-auto flex max-w-3xl flex-col items-center text-center">
        <InformationCircleIcon className="h-12 w-12 text-rcprimary-400" />
        <DialogTitle
          as="h2"
          className="mt-4 font-semibold text-2xl text-gray-950 sm:text-3xl"
        >
          {disclosureTitle({ product, patient: item?.patient })}
        </DialogTitle>
        <p className="mt-3 text-gray-900 text-lg">
          {intro || disclosureIntro({ product })}
        </p>
        {position && position.total > 1 && (
          <p className="mt-2 text-gray-500 text-sm">
            {productNoun(product) === 'subscription' ? 'Subscription' : 'Plan'}{' '}
            {position.index} of {position.total}. Each plan or subscription{' '}
            {positionScope} needs its own confirmation.
          </p>
        )}
      </div>

      <div className="mt-8">
        {loading || !product ? (
          <p className="text-center text-gray-500">
            Loading subscription options...
          </p>
        ) : tiers.length === 0 ? (
          <p className="rounded-lg border border-amber-300 bg-amber-50 p-4 text-center text-amber-900">
            Subscription pricing is not set up for your dental practice yet, so
            this cannot be confirmed. Please contact support.
          </p>
        ) : (
          <SubscriptionDisclosure
            product={product}
            tiers={tiers}
            submission={submission}
            onChange={setSubmission}
            errorMessages={errorMessages}
          />
        )}
      </div>

      {requiresCard && onAddCard && (
        <div className="mt-6 rounded-lg border border-amber-300 bg-amber-50 p-4 text-amber-900">
          <p className="font-semibold">
            A payment method is needed to confirm this plan.
          </p>
          <p className="mt-1 text-sm">
            Your first subscription charge comes soon after your first shipment,
            so we keep a card on file now. There is no charge today.
          </p>
          <button
            type="button"
            onClick={onAddCard}
            className="mt-3 inline-flex min-h-10 items-center justify-center rounded-lg bg-rcprimary-400 px-5 font-medium text-gray-950 hover:bg-rcprimary-300 focus-visible:outline-2 focus-visible:outline-rcprimary-400 focus-visible:outline-offset-2"
          >
            Add a payment method
          </button>
        </div>
      )}
      {showSoftCardPrompt && (
        <div className="mt-6 rounded-lg border border-gray-200 bg-gray-50 p-4 text-gray-800 text-sm">
          You have no payment method on file. Adding one now means your renewal
          goes through without interruption.{' '}
          <button
            type="button"
            onClick={onAddCard}
            className="font-semibold text-rcprimary-500 underline"
          >
            Add a payment method
          </button>
        </div>
      )}

      <div
        className={`mt-8 grid gap-3 sm:ml-auto sm:max-w-lg ${dismissable ? 'sm:grid-cols-2' : ''}`}
      >
        {dismissable && (
          <button
            type="button"
            className="inline-flex min-h-12 items-center justify-center rounded-lg border-2 border-rcprimary-400 bg-white px-6 font-medium text-gray-950 text-xl hover:bg-rcprimary-50 focus-visible:outline-2 focus-visible:outline-rcprimary-400 focus-visible:outline-offset-2"
            onClick={handleBack}
          >
            Back
          </button>
        )}
        <button
          type="button"
          className="inline-flex min-h-12 items-center justify-center rounded-lg bg-rcprimary-400 px-6 font-medium text-gray-950 text-xl hover:bg-rcprimary-300 focus-visible:outline-2 focus-visible:outline-rcprimary-400 focus-visible:outline-offset-2 disabled:cursor-not-allowed disabled:bg-gray-300 disabled:text-gray-500"
          disabled={!canContinue}
          onClick={handleContinue}
        >
          {isSubmitting ? 'Saving...' : 'I Agree, Continue'}
        </button>
      </div>
    </Modal>
  );
}

SubscriptionDisclosureModal.propTypes = {
  open: PropTypes.bool.isRequired,
  item: PropTypes.object,
  onBack: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  isSubmitting: PropTypes.bool,
  errorMessages: PropTypes.array,
  position: PropTypes.shape({
    index: PropTypes.number.isRequired,
    total: PropTypes.number.isRequired,
  }),
  dismissable: PropTypes.bool,
  intro: PropTypes.string,
  positionScope: PropTypes.string,
  requiresCard: PropTypes.bool,
  hasCard: PropTypes.bool,
  onAddCard: PropTypes.func,
  tiers: PropTypes.array,
  zdex: PropTypes.number,
};

export default SubscriptionDisclosureModal;
